import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Tag, Share2, Printer, ChevronLeft, ChevronRight, Clock, Eye } from 'lucide-react';
import PageTemplate from '../components/Common/PageTemplate';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton, FacebookIcon, TwitterIcon, LinkedinIcon, WhatsappIcon } from 'react-share';
import { motion, useScroll, useSpring } from 'framer-motion';
import duyurularData from '../data/Duyurular.json';

const AnnouncementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      
      // JSON dosyasından verileri yükle ve boş kayıtları filtrele
      const filteredData = duyurularData.filter(duyuru => 
        duyuru && 
        duyuru.title && 
        duyuru.title.trim() !== '' && 
        duyuru.summary && 
        duyuru.summary.trim() !== ''
      );

      // JSON field'larını beklenen field'lara map et
      const mappedData = filteredData.map((duyuru, index) => ({
        id: index + 1,
        title: duyuru.title,
        content: duyuru.summary, // summary'yi content olarak kullan
        summary: duyuru.summary,
        created_at: duyuru.date,
        category: duyuru.category,
        is_important: duyuru.category === 'Önemli' || duyuru.category === 'important',
        link: duyuru.link,
        reading_time: Math.max(1, Math.ceil(duyuru.summary.length / 200)) // Yaklaşık okuma süresi
      }));

      // Tarihe göre sırala (yeniden eskiye)
      const sortedAnnouncements = mappedData.sort((a, b) => {
        const dateA = parseDate(a.created_at);
        const dateB = parseDate(b.created_at);
        return dateB - dateA;
      });
      
      setAllAnnouncements(sortedAnnouncements);

      const currentAnn = sortedAnnouncements.find(a => a.id.toString() === id);
      if (currentAnn) {
        setAnnouncement(currentAnn);
      } else {
        setError('Duyuru bulunamadı.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    
    // Türkçe tarih formatını kontrol et (örn: "01.12.2024")
    if (dateString.includes('.')) {
      const [day, month, year] = dateString.split('.');
      if (day && month && year) {
        return new Date(year, month - 1, day);
      }
    }
    
    return new Date(dateString);
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const { previousAnnouncement, nextAnnouncement } = useMemo(() => {
    if (!announcement || allAnnouncements.length === 0) return { previousAnnouncement: null, nextAnnouncement: null };
    const currentIndex = allAnnouncements.findIndex(a => a.id === announcement.id);
    const prev = currentIndex > 0 ? allAnnouncements[currentIndex - 1] : null;
    const next = currentIndex < allAnnouncements.length - 1 ? allAnnouncements[currentIndex + 1] : null;
    return { previousAnnouncement: prev, nextAnnouncement: next };
  }, [announcement, allAnnouncements]);

  const handlePrint = () => window.print();
  
  if (loading) return <PageTemplate title="Yükleniyor..."><div className="text-center p-8">Yükleniyor...</div></PageTemplate>;
  if (error) return <PageTemplate title="Hata"><div className="text-center p-8 text-red-500">{error}</div></PageTemplate>;
  if (!announcement) return null;

  const shareUrl = window.location.href;
  const breadcrumbs = [
    { label: 'Ana Sayfa', href: '/' },
    { label: 'Duyurular', href: '/duyurular' },
    { label: announcement.title.length > 40 ? `${announcement.title.substring(0, 40)}...` : announcement.title }
  ];

  return (
    <>
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-hacettepe-blue origin-left z-50" style={{ scaleX }} />
      <PageTemplate title={announcement.title} breadcrumbs={breadcrumbs}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
                {announcement.is_important && (
                  <span className="flex items-center gap-1.5 bg-red-100 text-red-800 px-3 py-1 rounded-full font-semibold">
                    <Tag size={14} /> Önemli
                  </span>
                )}
                <div className="flex items-center gap-1.5"><Calendar size={14} />{new Date(announcement.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                <div className="flex items-center gap-1.5"><Clock size={14} />~{announcement.reading_time} dk okuma</div>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight">
                {announcement.title}
              </h1>
            </div>

            <div 
              className="prose prose-lg max-w-none text-gray-800 dark:text-gray-200 leading-relaxed p-8 md:p-12 border-t border-gray-200/50 dark:border-gray-700/50"
              dangerouslySetInnerHTML={{ __html: announcement.content }}
            />
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {previousAnnouncement ? (
                <Link to={`/duyurular/${previousAnnouncement.id}`} className="group block text-left">
                  <div className="h-full bg-white dark:bg-gray-800/50 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center"><ChevronLeft size={16} className="mr-1"/> Önceki Duyuru</p>
                      <h4 className="font-bold text-gray-800 dark:text-white group-hover:text-hacettepe-blue transition-colors">{previousAnnouncement.title}</h4>
                  </div>
                </Link>
            ) : <div/>}
            {nextAnnouncement && (
                <Link to={`/duyurular/${nextAnnouncement.id}`} className="group block text-right">
                  <div className="h-full bg-white dark:bg-gray-800/50 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center justify-end">Sonraki Duyuru <ChevronRight size={16} className="ml-1"/></p>
                      <h4 className="font-bold text-gray-800 dark:text-white group-hover:text-hacettepe-blue transition-colors">{nextAnnouncement.title}</h4>
                  </div>
                </Link>
            )}
          </div>
        </motion.div>
      </PageTemplate>
    </>
  );
};

export default AnnouncementDetail; 